/**
 * StatementEmailDialog — envoi d'un état financier (PDF) au client : destinataire, objet, message, mot de passe PDF optionnel.
 */
import { useState } from "react";
import { Loader2, Send } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { Btn, Field, Modal, errMsg, inputCls, useGlBase } from "../_shared";

const LABELS = { "income-statement": "État des résultats", "balance-sheet": "Bilan", "financial-statements": "États financiers" };

export default function StatementEmailDialog({ kind, query, defaultTo, period, onClose, onSent }) {
  const base = useGlBase();
  const [f, setF] = useState({ to: defaultTo || "", subject: "", message: "", password: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const send = async () => {
    if (!f.to.includes("@")) { toast.error("Adresse courriel du destinataire requise"); return; }
    setBusy(true);
    try {
      const r = await api.post(`${base}/reports/${kind}/email`, { ...f, ...query });
      toast.success(`${r.data.filename} envoyé à ${r.data.to}`);
      onSent?.(r.data);
      onClose();
    } catch (e) { toast.error(errMsg(e, "Courriel non envoyé")); onSent?.(); } finally { setBusy(false); }
  };

  return (
    <Modal title={`Envoyer par courriel — ${LABELS[kind]}`} sub={period ? `Période : ${period} · le PDF porte la bande du cabinet et le logo de l'entreprise.` : undefined} onClose={onClose} testId="statement-email-dialog"
      footer={<>
        <Btn tone="slate" onClick={onClose} testId="statement-email-cancel">Annuler</Btn>
        <Btn tone="sky" icon={busy ? Loader2 : Send} busy={busy} onClick={send} testId="statement-email-send">Envoyer</Btn>
      </>}>
      <Field label="Destinataire" hint={defaultTo ? "Pré-rempli avec le courriel des réglages Entreprise (ou du client principal)." : "Aucun courriel dans les réglages Entreprise — saisissez-le."}>
        <input type="email" className={inputCls} value={f.to} onChange={(e) => set("to", e.target.value)} placeholder="client@exemple.com" data-testid="statement-email-to" />
      </Field>
      <Field label="Objet" hint="Laisser vide pour l'objet automatique (rapport · entreprise · période).">
        <input className={inputCls} value={f.subject} onChange={(e) => set("subject", e.target.value)} data-testid="statement-email-subject" />
      </Field>
      <Field label="Message (facultatif)">
        <textarea className={`${inputCls} min-h-[90px]`} value={f.message} onChange={(e) => set("message", e.target.value)} placeholder="Quelques mots pour le client…" data-testid="statement-email-message" />
      </Field>
      <Field label="Mot de passe du PDF (facultatif)" hint="Si renseigné, le PDF est protégé et le courriel le mentionne — communiquez le mot de passe au client séparément.">
        <input className={`${inputCls} font-mono`} value={f.password} onChange={(e) => set("password", e.target.value)} autoComplete="off" data-testid="statement-email-password" />
      </Field>
    </Modal>
  );
}
